import { Button, Grid, TextField, Checkbox, MenuItem, FormControlLabel, Switch, DialogActions, Box, Typography, IconButton, } from '@mui/material';
import styles from './styles.module.css';
import CloseIcon from '@mui/icons-material/Close';
import React, { useState, useContext, useEffect } from 'react';
import CustomerService from 'services/CustomerService';
import { ShopContext } from 'Context/ShopContext';
import ShopsHelper from 'helpers/ShopsHelper';
import { AlertMessagesContext } from 'react-alert-messages';
import { ThemeContext } from 'Context/ThemeContext';
import NumberField from 'components/library/NumberField';
import { useTranslation } from 'react-i18next';

export default function EditCustomerDialog(props) {
  const { t: tCommon } = useTranslation('common');
  const TYPE = {
    CREDIT: 'Credit',
    ADVANCE: 'Advance',
  };

  const { shop } = useContext(ShopContext);
  const { closeDialog, editData, reloadCustomers, customerGroups } = props;
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const { theme } = useContext(ThemeContext);

  const [name, setName] = useState(editData.name);
  const [mobile, setMobile] = useState(editData.mobile);
  const [location, setLocation] = useState(editData.location);
  const [isActive, setIsActive] = useState(editData.is_active);
  const [creditLimit, setCreditLimit] = useState(editData.credit_limit);
  const [trn, setTrn] = useState(editData?.trn_number);
  const [code, setCode] = useState(editData?.code);
  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [group, setGroup] = useState(editData?.group?.id);
  const [balanceType, setBalanceType] = useState(editData.balance < 0 ? TYPE.CREDIT : TYPE.ADVANCE);
  const [balanceAmount, setBalanceAmount] = useState(Math.abs(editData.balance));
  const [creditType, setCreditType] = useState(editData.credit_limit < 0 ? TYPE.CREDIT : TYPE.ADVANCE);
  const [amount, setAmount] = useState(Math.abs(editData.credit_limit));
  const [order, setOrder] = useState(editData.order || null);
  const [enableLoyaltyPoints, setEnableLoyaltyPoints] = useState(editData.enable_loyalty_points || false);

  const handleUpdateCustomer = async () => {
    const adjustedBalance = balanceType === TYPE.CREDIT ? -Math.abs(balanceAmount) : Math.abs(balanceAmount);
    setLoadingIndicator(true);
    try {
      const data = {
        ...editData,
        name,
        mobile,
        location,
        is_active: isActive,
        credit_limit: Number(creditLimit),
        trn_number: trn,
        group_id: group,
        balance: adjustedBalance,
        code,
        order: order ? Number(order) : null,
        enable_loyalty_points: enableLoyaltyPoints,
      };
      await CustomerService.updateCustomer(editData.id, data);
      postAlertMessage({
        text: 'Customer updated successfully',
        type: 'success',
      });
      reloadCustomers();
      closeDialog();
    } catch (error) {
      postAlertMessage({ text: error.message, type: 'failed' });
    }
    setLoadingIndicator(false);
  };

  useEffect(() => {
    if (amount !== '') {
      const formattedAmount = ShopsHelper.getAmountFormatted(shop, amount);
      setCreditLimit(creditType === TYPE.CREDIT ? -1 * formattedAmount : formattedAmount);
    } else {
      setCreditLimit('');
    }
  }, [creditType, amount, shop, TYPE.CREDIT]);

  useEffect(() => {
    setBalanceAmount((prev) => ShopsHelper.getAmountFormatted(shop, Math.abs(prev)));
  }, [balanceType, shop, TYPE.CREDIT]);

  return (
    <Box className={styles.popUp}>
      <Box className={styles.contentWrapper}>
        <Box className={styles.headSec}>
          <Typography component="h2" className={styles.editTitle}>{tCommon('Edit Customer')}</Typography>
          <IconButton size="small" onClick={closeDialog}><CloseIcon /></IconButton>
        </Box>
        <Box className={styles.inputSec}>
          <Grid container className={styles.inputRow} alignItems="flex-start" spacing={2} justifyContent="flex-start">
            <Grid item xs={12} sm={6}>
              <TextField label={tCommon('Customer ID')} variant="outlined" size="small" fullWidth disabled value={editData.id} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={tCommon('Code')}
                name="code"
                size="small"
                variant="outlined"
                fullWidth
                value={code}
                onChange={(e) => {
                  setCode(e.target.value);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={tCommon('Customer Name')}
                name="name"
                variant="outlined"
                size="small"
                fullWidth
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={tCommon('Customer Mobile')}
                name="Mobile"
                size="small"
                variant="outlined"
                fullWidth
                value={mobile}
                onChange={(e) => {
                  setMobile(e.target.value);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={tCommon('Location')}
                name="location"
                size="small"
                variant="outlined"
                fullWidth
                value={location}
                onChange={(e) => {
                  setLocation(e.target.value);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={theme?.variable?.trn_label || tCommon('Tax ID')}
                name="tax"
                size="small"
                variant="outlined"
                fullWidth
                value={trn}
                onChange={(e) => {
                  setTrn(e.target.value.toUpperCase());
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={tCommon('Points')}
                name="points"
                variant="outlined"
                size="small"
                fullWidth
                disabled
                value={editData.points}
              />
            </Grid>
            <Grid item xs={12} sm={6} container alignItems="center">
              <Grid item xs={4}>
                <TextField
                  select
                  label={tCommon('Credit Type')}
                  variant="outlined"
                  size="small"
                  value={creditType}
                  onChange={(e) => setCreditType(e.target.value)}
                  className={styles.selectBox}
                  fullWidth
                >
                  <MenuItem value={TYPE.CREDIT}>{tCommon('Credit')}</MenuItem>
                  <MenuItem value={TYPE.ADVANCE}>{tCommon('Advance')}</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={8} style={{ paddingLeft: '4px' }}>
                <NumberField
                  label={tCommon('Amount')}
                  value={amount}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    if (value >= 0 || e.target.value === '') {
                      setAmount(e.target.value);
                    }
                  }}
                />
              </Grid>
            </Grid>
            <Grid item xs={12} sm={6} container alignItems="center">
              <Grid item xs={4} style={{ paddingLeft: 0 }}>
                <TextField
                  select
                  label={tCommon('Balance Type')}
                  variant="outlined"
                  size="small"
                  value={balanceType}
                  onChange={(e) => setBalanceType(e.target.value)}
                  className={styles.selectBox}
                  fullWidth
                  InputProps={{
                    style: {
                      color: balanceType === TYPE.CREDIT ? 'red' : 'green',
                    },
                  }}
                >
                  <MenuItem value={TYPE.CREDIT}>{tCommon('Credit')}</MenuItem>
                  <MenuItem value={TYPE.ADVANCE}>{tCommon('Advance')}</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={8} style={{ paddingLeft: '4px' }}>
                <NumberField
                  label={tCommon('Amount')}
                  value={balanceAmount}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    if (e.target.value === '' || val >= 0) {
                      setBalanceAmount(val);
                    }
                  }}
                />
              </Grid>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label={tCommon('Customer Group')}
                variant="outlined"
                size="small"
                value={group}
                onChange={(event) => setGroup(event.target.value)}
                fullWidth
              >
                <MenuItem value={null}>None</MenuItem>
                {customerGroups &&
                  customerGroups.map((cg) => (
                    <MenuItem value={cg.id} key={cg.id}>
                      {cg.name}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <NumberField
                label={tCommon('Sort Order')}
                value={order}
                onChange={(e) => {
                  setOrder(e.target.value);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                value="end"
                control={
                  <Checkbox
                    color="primary"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    inputProps={{ 'aria-label': 'controlled' }}
                  />
                }
                label={tCommon('Active Customer')}
                labelPlacement="end"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={enableLoyaltyPoints}
                    onChange={(e) => setEnableLoyaltyPoints(e.target.checked)}
                    color="primary"
                  />
                }
                label={tCommon('Enable Loyalty Points')}
              />
            </Grid>
          </Grid>
        </Box>
        <Box className={styles.footSec}>
          <DialogActions>
            <Button variant="contained" color="secondary" size="small" onClick={closeDialog}>
              {tCommon('Close')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              disabled={name === '' || mobile === '' || location === '' || loadingIndicator}
              onClick={handleUpdateCustomer}
            >
              {tCommon('Update Customer')}
            </Button>
          </DialogActions>
        </Box>
      </Box>
    </Box>
  );
}
